import { router, useForm, usePage } from '@inertiajs/react';
import { useId, useState } from 'react';
import type { FormEvent } from 'react';

import { ErrorSummary } from '@/components/public/forms/ErrorSummary';
import FormActions from '@/components/public/forms/FormActions';
import { SubmissionReceipt } from '@/components/public/forms/SubmissionReceipt';
import DatePicker from '@/components/public/ui/DatePicker';
import Select from '@/components/public/ui/Select';
import { trackUmamiEvent } from '@/lib/analytics';
import { buildErrorSummaryItems } from '@/lib/errorSummary';
import { inputClass, inputErrorClass, sectionLabel } from '@/lib/formStyles';
import { birthDateError, todayISODate, validateEmail, validatePhonePT } from '@/lib/validators';

type FlashProps = { flash: { notice?: string; alert?: string } };

type Payload = {
  certificateType: string;
  name: string;
  birthDate: string;
  documentType: string;
  documentNumber: string;
  documentTypeOther: string;
  phone: string;
  email: string;
  address: string;
  purpose: string;
  purposeOther: string;
  copies: string;
  pickupLocation: string;
};

const CERTIFICATE_TYPES = [
  'Residência e Naturalidade',
  'Vida',
  'Composição do Agregado Familiar',
  'Insuficiência Económica',
  'Situação de Desemprego',
  'Outro',
];

const FIELD_LABELS: Record<string, string> = {
  certificateType: 'Tipo de atestado',
  name: 'Nome completo',
  birthDate: 'Data de nascimento',
  documentType: 'Tipo de documento',
  documentNumber: 'N.º do documento',
  documentTypeOther: 'Tipo de documento',
  phone: 'Telefone',
  email: 'Correio eletrónico',
  address: 'Morada',
  purpose: 'Finalidade',
  purposeOther: 'Finalidade',
  copies: 'Número de cópias',
  pickupLocation: 'Local de levantamento',
  consent: 'Consentimento',
  files: 'Anexos',
  type: 'Tipo de pedido',
};

const FIELD_ANCHORS = new Set([
  'name',
  'birthDate',
  'documentType',
  'documentNumber',
  'documentTypeOther',
  'phone',
  'email',
  'address',
  'purpose',
  'purposeOther',
  'copies',
  'pickupLocation',
]);

export default function CertificatesForm() {
  const { flash, pickupLocations } = usePage<
    FlashProps & { pickupLocations: { value: string; label: string }[] }
  >().props;
  const idPrefix = useId();
  const fid = (name: string) => `${idPrefix}-${name}`;
  const errId = (name: string) => `${idPrefix}-${name}-error`;

  const form = useForm<{ payload: Payload; files: File[]; consent: boolean; consented_at: string }>(
    {
      payload: {
        certificateType: '',
        name: '',
        birthDate: '',
        documentType: '',
        documentNumber: '',
        documentTypeOther: '',
        phone: '',
        email: '',
        address: '',
        purpose: '',
        purposeOther: '',
        copies: '1',
        pickupLocation: '',
      },
      files: [],
      consent: false,
      consented_at: '',
    },
  );

  const { data, setData, processing, errors: rawErrors, reset } = form;
  const errors = rawErrors as unknown as Record<string, string | undefined>;

  const [clientErrors, setClientErrors] = useState<Record<string, string>>({});

  const set =
    (field: keyof Payload) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setData('payload', { ...data.payload, [field]: e.target.value });

  const validateField = (field: string, value: string): string | null => {
    if (!value) return null;
    if (field === 'phone' && !validatePhonePT(value)) return 'Número de telefone inválido.';
    if (field === 'email' && !validateEmail(value)) return 'Correio eletrónico inválido.';
    if (field === 'birthDate') return birthDateError(value);
    return null;
  };

  const handleBlur = (field: keyof Payload) => () => {
    const value = String(data.payload[field]);
    const err = validateField(field, value);
    setClientErrors((prev) => {
      const next = { ...prev };
      if (err) next[field] = err;
      else delete next[field];
      return next;
    });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    const phoneErr = validateField('phone', data.payload.phone);
    if (phoneErr) errs.phone = phoneErr;
    const emailErr = validateField('email', data.payload.email);
    if (emailErr) errs.email = emailErr;
    const birthErr = validateField('birthDate', data.payload.birthDate);
    if (birthErr) errs.birthDate = birthErr;
    if (Object.keys(errs).length > 0) {
      setClientErrors(errs);
      trackUmamiEvent('form_submit_blocked', {
        form_type: 'certificate',
        error_count: Object.keys(errs).length,
      });
      return;
    }
    form.transform((d) => ({
      type: 'certificate',
      consent: 'true',
      consented_at: new Date().toISOString(),
      payload: d.payload,
      files: d.files,
    }));
    trackUmamiEvent('service_request_submit', {
      service_type: 'certificate',
      has_files: data.files.length > 0,
    });
    form.post('/service-requests', { forceFormData: true });
  };

  const fieldErr = (field: string) => clientErrors[field] || errors[field];
  const errorItems = buildErrorSummaryItems({ ...errors, ...clientErrors }, FIELD_LABELS, (key) =>
    FIELD_ANCHORS.has(key) ? `#${fid(key)}` : undefined,
  );

  const p = data.payload;
  const isFormValid =
    !!p.certificateType &&
    !!p.name &&
    !!p.birthDate &&
    !!p.documentType &&
    !!p.documentNumber &&
    (p.documentType !== 'outro' || !!p.documentTypeOther.trim()) &&
    !!p.phone &&
    !!p.email &&
    !!p.address &&
    !!p.purpose &&
    (p.purpose !== 'outro' || !!p.purposeOther.trim()) &&
    !!p.copies &&
    !!p.pickupLocation &&
    data.consent &&
    Object.keys(clientErrors).length === 0;

  if (flash?.notice) {
    return (
      <SubmissionReceipt
        formType="certificate"
        title="Pedido submetido à Junta"
        message={flash.notice}
        steps={[
          { label: 'Registo', description: 'O pedido ficou guardado com data e consentimento.' },
          { label: 'Análise', description: 'Os serviços confirmam os dados e a finalidade.' },
          {
            label: 'Contacto',
            description: 'Os serviços entrarão em contacto se forem necessários esclarecimentos.',
          },
        ]}
        actions={[
          {
            label: 'Submeter outro pedido',
            onClick: () => {
              reset();
              setClientErrors({});
              router.get(window.location.pathname, {}, { replace: true, preserveScroll: true });
            },
          },
          { label: 'Voltar aos serviços', href: '/services', variant: 'primary' },
        ]}
      />
    );
  }

  return (
    <div className="space-y-4">
      <form onSubmit={submit} className="space-y-8">
        <ErrorSummary items={errorItems} />

        {/* Tipo de Atestado */}
        <div>
          <span className={sectionLabel('secondary')}>Tipo de Atestado</span>
          <div className="flex flex-wrap gap-2">
            {CERTIFICATE_TYPES.map((type) => (
              <label
                key={type}
                className={`flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors select-none ${
                  p.certificateType === type
                    ? 'border-secondary/40 bg-secondary/10 text-secondary font-medium'
                    : 'text-on-surface-light hover:border-secondary/40 border-line bg-surface'
                }`}
              >
                <input
                  type="radio"
                  name="certificateType"
                  className="sr-only"
                  checked={p.certificateType === type}
                  onChange={() => setData('payload', { ...p, certificateType: type })}
                  required
                />
                {type}
              </label>
            ))}
          </div>
        </div>

        {/* Dados Pessoais */}
        <div>
          <span className={sectionLabel('secondary')}>Dados Pessoais</span>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('name')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Nome completo
              </label>
              <input
                id={fid('name')}
                className={inputClass}
                value={p.name}
                onChange={set('name')}
                placeholder="Ex: Maria da Silva"
                required
              />
            </div>
            <div>
              <label
                htmlFor={fid('birthDate')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Data de Nascimento
              </label>
              <DatePicker
                id={fid('birthDate')}
                value={p.birthDate}
                onChange={(value) => {
                  setData('payload', { ...p, birthDate: value });
                  const err = validateField('birthDate', value);
                  setClientErrors((prev) => {
                    const next = { ...prev };
                    if (err) next.birthDate = err;
                    else delete next.birthDate;
                    return next;
                  });
                }}
                max={todayISODate()}
                hasError={!!fieldErr('birthDate')}
                aria-invalid={!!fieldErr('birthDate')}
                aria-describedby={fieldErr('birthDate') ? errId('birthDate') : undefined}
                required
              />
              {fieldErr('birthDate') && (
                <p id={errId('birthDate')} className="text-error mt-1 text-xs">
                  {fieldErr('birthDate')}
                </p>
              )}
            </div>
          </div>
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('documentType')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Tipo de Documento
              </label>
              <Select
                id={fid('documentType')}
                value={p.documentType}
                onChange={(e) => {
                  const next = e.target.value;
                  setData('payload', {
                    ...p,
                    documentType: next,
                    documentTypeOther: next === 'outro' ? p.documentTypeOther : '',
                  });
                }}
                required
              >
                <option value="">Selecionar</option>
                <option value="cc">Cartão de Cidadão</option>
                <option value="passaporte">Passaporte</option>
                <option value="residencia">Título de Residência</option>
                <option value="outro">Outro</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('documentNumber')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                N.º do documento
              </label>
              <input
                id={fid('documentNumber')}
                className={inputClass}
                value={p.documentNumber}
                onChange={set('documentNumber')}
                placeholder="Ex: 12345678 9 ZZ4"
                required
              />
            </div>
          </div>
          {p.documentType === 'outro' && (
            <div className="mb-4">
              <label
                htmlFor={fid('documentTypeOther')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Especifique o tipo de documento
              </label>
              <input
                id={fid('documentTypeOther')}
                className={inputClass}
                value={p.documentTypeOther}
                onChange={set('documentTypeOther')}
                placeholder="Ex: Carta de Condução, Passaporte estrangeiro…"
                required
              />
            </div>
          )}
          <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor={fid('phone')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Telefone
              </label>
              <input
                id={fid('phone')}
                type="tel"
                className={fieldErr('phone') ? inputErrorClass : inputClass}
                value={p.phone}
                onChange={set('phone')}
                onBlur={handleBlur('phone')}
                aria-invalid={!!fieldErr('phone')}
                aria-describedby={fieldErr('phone') ? errId('phone') : undefined}
                placeholder="+351 9XX XXX XXX"
                required
              />
              {fieldErr('phone') && (
                <p id={errId('phone')} className="text-error mt-1 text-xs">
                  {fieldErr('phone')}
                </p>
              )}
            </div>
            <div>
              <label
                htmlFor={fid('email')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Correio eletrónico
              </label>
              <input
                id={fid('email')}
                type="email"
                className={fieldErr('email') ? inputErrorClass : inputClass}
                value={p.email}
                onChange={set('email')}
                onBlur={handleBlur('email')}
                aria-invalid={!!fieldErr('email')}
                aria-describedby={fieldErr('email') ? errId('email') : undefined}
                placeholder="email@exemplo.pt"
                required
              />
              {fieldErr('email') && (
                <p id={errId('email')} className="text-error mt-1 text-xs">
                  {fieldErr('email')}
                </p>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor={fid('address')}
              className="text-on-surface mb-1.5 block text-sm font-medium"
            >
              Morada Completa
            </label>
            <input
              id={fid('address')}
              className={inputClass}
              value={p.address}
              onChange={set('address')}
              placeholder="Rua, n.º, código postal, localidade"
              required
            />
          </div>
        </div>

        {/* Pedido */}
        <div>
          <span className={sectionLabel('secondary')}>Pedido</span>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <div>
              <label
                htmlFor={fid('purpose')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Finalidade
              </label>
              <Select
                id={fid('purpose')}
                value={p.purpose}
                onChange={(e) => {
                  const next = e.target.value;
                  setData('payload', {
                    ...p,
                    purpose: next,
                    purposeOther: next === 'outro' ? p.purposeOther : '',
                  });
                }}
                required
              >
                <option value="">Selecionar</option>
                <option value="seguranca-social">Segurança Social</option>
                <option value="banco">Banco / Crédito</option>
                <option value="escola">Escola / Universidade</option>
                <option value="trabalho">Trabalho</option>
                <option value="financas">Finanças</option>
                <option value="outro">Outro</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('copies')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Nº de Exemplares
              </label>
              <Select id={fid('copies')} value={p.copies} onChange={set('copies')} required>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
              </Select>
            </div>
            <div>
              <label
                htmlFor={fid('pickupLocation')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Local de levantamento
              </label>
              <Select
                id={fid('pickupLocation')}
                value={p.pickupLocation}
                onChange={set('pickupLocation')}
                required
              >
                <option value="">Selecionar</option>
                {pickupLocations.map((loc) => (
                  <option key={loc.value} value={loc.value}>
                    {loc.label}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {p.purpose === 'outro' && (
            <div className="mt-4">
              <label
                htmlFor={fid('purposeOther')}
                className="text-on-surface mb-1.5 block text-sm font-medium"
              >
                Especifique a finalidade
              </label>
              <input
                id={fid('purposeOther')}
                className={inputClass}
                value={p.purposeOther}
                onChange={set('purposeOther')}
                placeholder="Ex: Processo de cidadania, candidatura a apoio…"
                required
              />
            </div>
          )}
        </div>

        {/* Consent */}
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={data.consent}
            onChange={(e) => setData('consent', e.target.checked)}
            className="mt-1 rounded"
            required
          />
          <span className="text-on-surface-light text-sm leading-relaxed">
            Declaro que os dados fornecidos são verdadeiros e tomei conhecimento do tratamento dos
            meus dados pessoais para efeitos deste pedido, nos termos da política de privacidade.
          </span>
        </label>

        <FormActions
          submitLabel="Submeter pedido"
          submittingLabel="A submeter…"
          submittingHint="A enviar os dados de forma segura"
          processing={processing}
          disabled={!isFormValid}
          onReset={() => {
            reset();
            setClientErrors({});
          }}
        />
      </form>
    </div>
  );
}
